import type { APIResponse } from '@m/utils/http/types';

/**
 * One place that turns site-api's verification-code errors into a stable reason (for Heap) and
 * into the copy users see, so every phone screen says the same thing for the same failure.
 */

const ERROR_REASONS: [RegExp, string][] = [
  [/Invalid phone number/i, 'InvalidPhoneNumber'],
  [/Did not find matching verification code/i, 'InvalidCode'],
  [/Too many failed attempts/i, 'TooManyAttempts'],
  [/Too many requests|rate limit exceeded/i, 'RateLimited'],
  // site-api sends "Verification code not found" when no code was sent in the last 30 minutes.
  [/No verification code found|Verification code not found/i, 'CodeNotFound'],
  [/does not match OTP-verified user/i, 'SignedInAsOtherUser'],
  [/already associated with another user/i, 'PhoneInUse'],
  [/Cannot initiate sign-up for registered user/i, 'AlreadyRegistered'],
  [/Verification (check|request) failed|missing verification ID/i, 'ProviderError'],
];

/**
 * site-api rejects a request body that fails validation (e.g. a phone number it can't parse) with
 * `{ errors: { "obj.phoneNumber": ["Invalid phone number!"] } }` and no `error`, so callers that
 * only read `error` saw nothing. Fills `error` from the first validation message in that case.
 */
export const withValidationError = <Data>(response: APIResponse<Data>): APIResponse<Data> => {
  if (response.success || response.error || !response.errors) return response;
  const messages: unknown[] = Object.values(response.errors).flat();
  const first = messages.find((message): message is string => typeof message === 'string');
  return first ? { ...response, error: first } : response;
};

/** A stable reason for a failed send or check: the `HCAPTCHA_*` code, a known case, or `Other`. */
export const getVerificationErrorReason = (error?: string) => {
  const hcaptchaCode = error?.match(/HCAPTCHA_[A-Z_]+/)?.[0];
  if (hcaptchaCode) return hcaptchaCode;
  return ERROR_REASONS.find(([pattern]) => error && pattern.test(error))?.[1] ?? 'Other';
};

const VERIFICATION_UNAVAILABLE =
  'Verification is temporarily unavailable. Please try again shortly.';
const PHONE_IN_USE = 'User already exists with this phone number.';

const SEND_ERROR_MESSAGES: Record<string, string> = {
  AlreadyRegistered: PHONE_IN_USE,
  HCAPTCHA_MISCONFIGURED: VERIFICATION_UNAVAILABLE,
  HCAPTCHA_REQUIRED: 'Please complete the human verification to continue.',
  HCAPTCHA_SERVICE_UNAVAILABLE: VERIFICATION_UNAVAILABLE,
  HCAPTCHA_VERIFICATION_FAILED: 'Human verification failed. Please check the box again.',
  InvalidPhoneNumber: 'Please enter a valid phone number.',
  PhoneInUse: PHONE_IN_USE,
  RateLimited: 'Too many attempts. Please wait a moment and try again.',
};

/** What to tell the user when sending (or resending) a verification code fails. */
export const getPhoneSendErrorMessage = (error?: string) =>
  SEND_ERROR_MESSAGES[getVerificationErrorReason(error)] ??
  'Phone number verification failed. Please try again.';

const CHECK_ERROR_MESSAGES: Record<string, string> = {
  CodeNotFound: 'That code has expired. Tap “Resend code” to get a new one.',
  InvalidCode: 'That code isn’t right. Check it and try again.',
  ProviderError: VERIFICATION_UNAVAILABLE,
  RateLimited: SEND_ERROR_MESSAGES.RateLimited,
  // The code step has no sign-out, so point back to phone entry (its Back button).
  SignedInAsOtherUser:
    'This number belongs to a different account from the one you’re signed in to. Go back and use the number on your account.',
  TooManyAttempts: 'Too many incorrect codes. Tap “Resend code” to get a new one.',
};

/** What to tell the user when checking a verification code fails. */
export const getPhoneCheckErrorMessage = (error?: string) =>
  CHECK_ERROR_MESSAGES[getVerificationErrorReason(error)] ??
  'We couldn’t verify your code. Please try again.';
